// TournamentSession.tsx (TF1) - the CONTINUOUS tournament surface. Where <TournamentPlay> renders one
// frozen ICM call-off spot, this plays a whole running tournament off the engine field simulation
// (engine/tournamentSession.ts): a shrinking multi-table field, hero's stack/level/M/ICM evolving
// hand-to-hand, the signature FIRST-IN JAM-OR-FOLD wired live through PF5's recommendPreflop (the DT Nash
// charts), named save/resume, and a narrated hand served from the ST-LIB library at $0. It reuses the
// ST-UI context strip (<TournamentContext>) and the S22 grade colors, so a tournament decision reads in
// the same visual language as a cash one.
import { useEffect, useMemo, useState } from 'react';
import { TournamentContext } from './TournamentContext';
import {
  applyHeroAction,
  dealHeroSpot,
  startTournament,
  type HeroAction,
  type HeroSpot,
  type ResolvedStep,
  type TournamentSessionState,
} from '../engine/tournamentSession';
import {
  deleteRun,
  listRuns,
  loadRun,
  saveRun,
  type SavedRunSummary,
} from './tournamentSave';
import { narratedTournamentHand, type NarratedTournamentHand } from './tournamentNarratedHand';
import type { TournamentPresetId } from '../engine/tournamentRunner';
import type { Stage } from '../components/StagePill';

interface TournamentSessionProps {
  presetId: TournamentPresetId;
  stage: Stage;
  /** bumped by the top-bar "New hand" control to start a fresh run. */
  runNonce: number;
}

const presetLabel = (id: TournamentPresetId): string => (id === 'local_200' ? '$200' : '$100');
const defaultRunName = (id: TournamentPresetId, nonce: number): string =>
  `${presetLabel(id)} Local #${nonce + 1}`;

const SUIT_GLYPH: Record<string, string> = { c: '♣', d: '♦', h: '♥', s: '♠' };
const suitColor = (s: string): string => (s === 'h' || s === 'd' ? 'var(--mist)' : 'var(--ink)');

/** One hero card as a felt chip (e.g. "A♠"). */
function Card({ cs }: { cs: string }) {
  const rank = cs[0] === 'T' ? '10' : cs[0];
  const suit = cs[1]!;
  return (
    <span className="tcard" style={{ color: suitColor(suit) }}>
      {rank}
      {SUIT_GLYPH[suit] ?? suit}
    </span>
  );
}

export function TournamentSession({ presetId, stage, runNonce }: TournamentSessionProps) {
  const [session, setSession] = useState<TournamentSessionState | null>(null);
  const [phase, setPhase] = useState<'decide' | 'result'>('decide');
  const [lastStep, setLastStep] = useState<ResolvedStep | null>(null);
  const [saved, setSaved] = useState<SavedRunSummary[]>([]);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [narrated, setNarrated] = useState<NarratedTournamentHand | null>(null);

  // Start a fresh run whenever the preset or the new-run nonce changes. bootToShort so the signature
  // first-in jam-or-fold is playable from hand one (a "resume mid-tournament, short stack" entry).
  useEffect(() => {
    const seed = `tf1-${presetId}-${runNonce}`;
    setSession(
      startTournament({ presetId, seed, runName: defaultRunName(presetId, runNonce), bootToShort: true }),
    );
    setPhase('decide');
    setLastStep(null);
    setNarrated(null);
    setSavedMsg(null);
    setSaved(listRuns());
  }, [presetId, runNonce]);

  const spot: HeroSpot | null = useMemo(() => (session ? dealHeroSpot(session) : null), [session]);

  if (!session || !spot) return null;

  const finished = session.finished;

  const act = (a: HeroAction) => {
    const step = applyHeroAction(session, spot, a);
    setLastStep(step);
    setPhase('result');
  };
  const nextHand = () => {
    if (!lastStep) return;
    setSession(lastStep.state);
    setPhase('decide');
    setLastStep(null);
    setNarrated(null);
  };
  const doSave = () => {
    saveRun(session);
    setSaved(listRuns());
    setSavedMsg(`Saved "${session.runName}".`);
  };
  const doResume = (name: string) => {
    const s = loadRun(name);
    if (s) {
      setSession(s);
      setPhase('decide');
      setLastStep(null);
      setNarrated(null);
      setSavedMsg(`Resumed "${s.runName}".`);
    }
  };
  const doDelete = (name: string) => {
    deleteRun(name);
    setSaved(listRuns());
  };
  const doNarrate = () => {
    setNarrated(narratedTournamentHand(`${session.seed}-narr-${session.field.handNo}`));
  };

  const rec: HeroAction = spot.recommendation?.finalIntent === 'jam' ? 'jam' : 'fold';

  return (
    <div className="tourney" data-testid="tournament-session">
      {/* Run bar: identity + save/resume + the live field ticker */}
      <div className="trunbar">
        <div className="trun-name">{session.runName}</div>
        <div className="trun-actions">
          <button type="button" className="tsavebtn" onClick={doSave}>
            Save
          </button>
        </div>
      </div>
      {savedMsg && <div className="tsaved-msg">{savedMsg}</div>}
      {saved.length > 0 && (
        <div className="tresume" data-testid="resume-picker">
          <div className="tresume-h">Saved runs</div>
          <div className="tresume-list">
            {saved.map((r) => (
              <div key={r.runName} className="tresume-row">
                <button type="button" className="tresume-load" onClick={() => doResume(r.runName)}>
                  <b>{r.runName}</b>
                  <span className="s">
                    L{r.level} . {r.playersLeft}/{r.fieldSize} left . {r.heroChips.toLocaleString()} chips
                    {r.finished ? ` . ${r.result}` : ''}
                  </span>
                </button>
                <button type="button" className="tresume-del" aria-label={`Delete ${r.runName}`} onClick={() => doDelete(r.runName)}>
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <TournamentContext c={spot.context} />

      {/* Hero hand */}
      <div className="thand">
        <div className="thand-cards">
          <Card cs={spot.heroCardStrings[0]} />
          <Card cs={spot.heroCardStrings[1]} />
        </div>
        <div className="thand-meta">
          <span className="pos">{spot.heroPosition}</span>
          <span className="lab">{spot.handLabel}</span>
        </div>
      </div>

      {/* The decision */}
      {finished ? (
        <div className="tverdict">
          <div className="tv-h">{session.result === 'won' ? 'You won the tournament' : 'Busted'}</div>
          {session.result === 'won'
            ? 'You outlasted the field. Bump "New hand" up top to run it back.'
            : `Out in ${session.field.stacks.length <= session.field.paidPlaces ? 'the money' : `${spot.context.playersLeft + 1}th`}. Run it back from the top bar.`}
        </div>
      ) : phase === 'result' && lastStep ? (
        // result panel FIRST (before the depth branch) so a "Next hand" control always appears - both
        // push/fold and deep hands settle here after acting.
        <TournamentResult
          step={lastStep}
          recommended={rec}
          stage={stage}
          spot={spot}
          onNext={nextHand}
        />
      ) : !spot.isPushFold ? (
        <>
          <div className="tpanel">
            <div className="tcondline">
              You are <b>{spot.context.effectiveStackBB.toFixed(1)} BB</b> - deeper than push/fold depth, so
              this is a full-poker spot, not a jam-or-fold. Fold on and let the blinds climb into jam range.
            </div>
          </div>
          <div className="tactrow">
            <button type="button" className="tact" onClick={() => act('fold')}>
              Fold on
              <span className="s">let the blinds rise</span>
            </button>
          </div>
        </>
      ) : (
        <>
          {/* PF5 provenance line - the LIVE proof ctx.pushFold was injected and the DT Nash path answered. */}
          <div className="tpf5" data-pf5={spot.pf5Engaged ? 'engaged' : 'off'} data-chart={spot.chartId ?? ''} data-band={spot.band ?? ''}>
            First-in jam-or-fold . {spot.pf5Engaged ? `PF5 Nash: ${spot.chartId} @ ${spot.band}` : 'PF5 not engaged'}
          </div>
          {stage === 'observe' && (
            <div className="tpanel">
              <div className="tcoach">
                The chart says <b>{rec === 'jam' ? 'JAM' : 'FOLD'}</b> here with {spot.handLabel} from{' '}
                {spot.heroPosition} at {spot.context.effectiveStackBB.toFixed(1)} BB. The DT Nash push/fold
                range ({spot.chartId} @ {spot.band}) is the ground truth at this depth.
              </div>
            </div>
          )}
          <div className="tactrow">
            <button type="button" className="tact" onClick={() => act('jam')}>
              Jam
              <span className="s">{spot.context.effectiveStackBB.toFixed(0)} BB all-in</span>
            </button>
            <button type="button" className="tact" onClick={() => act('fold')}>
              Fold
              <span className="s">give up the blinds</span>
            </button>
          </div>
        </>
      )}

      {/* Narrated hand ($0 ST-LIB consumer) */}
      <div className="tnarr">
        <button type="button" className="tnarr-btn" onClick={doNarrate}>
          Watch a narrated hand ($0, offline)
        </button>
        {narrated && (
          <div className="tnarr-body" data-testid="narrated-hand" data-narr-key={narrated.key}>
            <div className="tnarr-h">
              {narrated.bucket.street} . {narrated.bucket.handClass.replace(/_/g, ' ')} . {narrated.bucket.position}
              {narrated.bucket.icmPressure ? ` . ICM ${narrated.bucket.icmPressure}` : ''}
            </div>
            <div className="tnarr-p">
              <b>Beginner:</b> {narrated.coach.beginner_thought}
            </div>
            <div className="tnarr-p">
              <b>Pro:</b> {narrated.coach.pro_thought}
            </div>
            {narrated.coach.villain_read && (
              <div className="tnarr-p">
                <b>Read:</b> {narrated.coach.villain_read}
              </div>
            )}
            <div className="tnarr-src">served from ST-LIB library . key {narrated.key.slice(0, 22)}… . 0 API calls</div>
          </div>
        )}
      </div>

      {!finished && phase === 'decide' && (
        <div className="tfield-ticker">{session.lastEvent}</div>
      )}
    </div>
  );
}

/** The post-action panel: adherence grade (Diagnose), or a banked note (Solo), plus the chip outcome and
 *  the field advance, then Next hand. */
function TournamentResult({
  step,
  recommended,
  stage,
  spot,
  onNext,
}: {
  step: ResolvedStep;
  recommended: HeroAction;
  stage: Stage;
  spot: HeroSpot;
  onNext: () => void;
}) {
  const up = step.correct;
  // A deep hand (not a push/fold spot) has no Nash chart to grade against - just show the advance.
  if (!spot.isPushFold) {
    return (
      <>
        <div className="tverdict">
          <div className="tv-h">Folded on</div>
          {step.outcome} Deeper than jam-or-fold depth - the blinds climb toward push/fold range.
        </div>
        <div className="tnext">
          <button type="button" onClick={onNext}>
            {step.state.finished ? 'See result' : 'Next hand'}
          </button>
        </div>
      </>
    );
  }
  return (
    <>
      {stage === 'solo' ? (
        <div className="tverdict">
          <div className="tv-h">Solo . logged</div>
          {step.outcome} No inline grade in Solo - you flew it.
        </div>
      ) : (
        <div className={'tgrade ' + (up ? 'up' : 'down')}>
          <div className="tgrade-two">
            <div className="taxis">
              <div className="a">Your play</div>
              <div className="b" style={{ color: up ? 'var(--best)' : 'var(--mist)' }}>
                {up ? 'Matches the Nash chart' : 'Off the Nash chart'}
              </div>
            </div>
            <div className="taxis">
              <div className="a">Chart says</div>
              <div className="b">{recommended === 'jam' ? 'JAM' : 'FOLD'}</div>
            </div>
          </div>
          <div className="tcoach">
            {up
              ? `Correct - the DT Nash range (${spot.chartId} @ ${spot.band}) ${recommended === 'jam' ? 'jams' : 'folds'} this. `
              : `The chart ${recommended === 'jam' ? 'jams' : 'folds'} here (${spot.chartId} @ ${spot.band}); you did the other. `}
            {step.outcome}
            {step.contestedEquity !== null && ` (${Math.round(step.contestedEquity * 100)}% when called.)`}
          </div>
        </div>
      )}
      <div className="tnext">
        <button type="button" onClick={onNext}>
          {step.state.finished ? 'See result' : 'Next hand'}
        </button>
      </div>
    </>
  );
}
